import type { MidiEvent, MidiMessageType, MidiSource } from './types';

export const STATUS = {
  NOTE_OFF: 0x80,
  NOTE_ON: 0x90,
  POLY_PRESSURE: 0xa0,
  CONTROL_CHANGE: 0xb0,
  PROGRAM_CHANGE: 0xc0,
  CHANNEL_PRESSURE: 0xd0,
  PITCH_BEND: 0xe0,
} as const;

const A4_NOTE = 69;
const A4_HZ = 440;

/** Equal temperament, A4 = 440 Hz. */
export function noteToFrequency(note: number): number {
  return A4_HZ * 2 ** ((note - A4_NOTE) / 12);
}

function typeForStatus(status: number, data2: number): MidiMessageType {
  switch (status & 0xf0) {
    case STATUS.NOTE_OFF:
      return 'noteoff';
    // A note-on with zero velocity is the running-status form of note-off.
    case STATUS.NOTE_ON:
      return data2 === 0 ? 'noteoff' : 'noteon';
    case STATUS.POLY_PRESSURE:
      return 'polypressure';
    case STATUS.CONTROL_CHANGE:
      return 'cc';
    case STATUS.PROGRAM_CHANGE:
      return 'programchange';
    case STATUS.CHANNEL_PRESSURE:
      return 'channelpressure';
    case STATUS.PITCH_BEND:
      return 'pitchbend';
    default:
      return status === 0xf8 ? 'clock' : 'unknown';
  }
}

/** Parses a raw Web MIDI payload into a `MidiEvent`. */
export function parseMessage(
  data: Uint8Array,
  source: MidiSource,
  timestamp = performance.now(),
): MidiEvent {
  const status = data[0] ?? 0;
  const data1 = data[1] ?? 0;
  const data2 = data[2] ?? 0;
  const type = typeForStatus(status, data2);

  const ratio =
    type === 'pitchbend' ? ((data2 << 7) | data1) / 16383 : data2 / 127;

  return {
    type,
    channel: (status & 0x0f) + 1,
    value: data1,
    velocity: data2,
    frequency: noteToFrequency(data1),
    ratio,
    data,
    source,
    timestamp,
  };
}

function statusByte(command: number, channel: number): number {
  return command | (Math.max(1, Math.min(16, channel)) - 1);
}

export function noteOn(note: number, velocity = 100, channel = 1, source: MidiSource = 'pointer'): MidiEvent {
  return parseMessage(new Uint8Array([statusByte(STATUS.NOTE_ON, channel), note, velocity]), source);
}

export function noteOff(note: number, velocity = 0, channel = 1, source: MidiSource = 'pointer'): MidiEvent {
  return parseMessage(new Uint8Array([statusByte(STATUS.NOTE_OFF, channel), note, velocity]), source);
}

